import { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";

import "./PrivacyPolicy.css";

const sections = [
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "sharing", label: "Sharing Your Information" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "data-security", label: "Data Security" },
  { id: "childrens-privacy", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("information-we-collect");
  const sectionRefs = useRef({});

  useEffect(() => {
    const scroller = document.querySelector(".legalLayoutRoot");

    const handleScroll = () => {
      const OFFSET = 120;
      let currentSection = sections[0].id;
      for (const { id } of sections) {
        const el = sectionRefs.current[id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= OFFSET) {
            currentSection = id;
          }
        }
      }
      setActiveSection(currentSection);
    };

    const target = scroller || window;
    target.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <Box className="legalPageWrap legalPrivacyWrap">
      {/* Sticky Sidebar */}
      <Box component="aside" className="legalSidebar">
        <Box component="nav">
          <Box component="ul" className="legalSidebarList">
            {sections.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <Box component="li" key={id} className="legalSidebarItem">
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`legalSidebarBtn ${isActive ? "isActive" : ""}`}
                    type="button"
                  >
                    {label}
                  </button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box component="main" className="legalMain">
        <Typography className="legalH1" component="h1">
          Privacy Policy
        </Typography>
        <Typography className="legalEffective" component="p">
          Effective: May 1, 2026
        </Typography>

        <Box className="legalInfoBanner legalInfoBanner--blue">
          <Box className="legalInfoBannerStripe legalInfoBannerStripe--blue" />
          <Typography
            className="legalInfoBannerText legalInfoBannerText--blue"
            component="p"
          >
            This Privacy Policy explains how QGroup ("Quest", "we", "us", or
            "our") collects, uses, and protects your personal information when
            you use our web application. By accessing Quest, you agree to the
            practices described in this policy.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="information-we-collect"
          ref={setRef("information-we-collect")}
          className="legalSection"
        >
          <SectionTitle number={1} title="Information We Collect" />
          <SubTitle>Information you provide directly:</SubTitle>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText legalListHasBottomLarge"
          >
            <li>
              Account registration details such as your name, email address, and
              password
            </li>
            <li>
              Profile information you choose to add, including organisation name
              and contact details
            </li>
            <li>
              Communications you send to us, including support requests and
              feedback
            </li>
            <li>
              Content and data you upload or create while using the Quest
              platform
            </li>
          </Box>
          <SubTitle>Information collected automatically:</SubTitle>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              Log data including IP address, browser type, pages visited, and
              time spent on pages
            </li>
            <li>
              Device information such as operating system and screen resolution
            </li>
            <li>
              Usage data and feature interaction patterns to improve our service
            </li>
            <li>Cookies and similar tracking technologies (see Section 04)</li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="how-we-use"
          ref={setRef("how-we-use")}
          className="legalSection"
        >
          <SectionTitle number={2} title="How We Use Your Information" />
          <Typography className="legalBody legalBodyBlack" component="p">
            We use the information we collect for the following purposes:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              To create and manage your account and provide you with our core
              services
            </li>
            <li>
              To process transactions and send related information, including
              purchase confirmations
            </li>
            <li>
              To respond to your comments, questions, and customer support
              requests
            </li>
            <li>
              To send administrative information, such as updates to our terms
              and policies
            </li>
            <li>
              To analyse usage trends and improve the functionality and
              performance of Quest
            </li>
            <li>
              To detect, investigate, and prevent fraudulent transactions and
              other illegal activities
            </li>
            <li>
              To comply with legal obligations and enforce our Terms of Service
            </li>
            <li>
              To send marketing and promotional communications, where you have
              opted in
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="sharing"
          ref={setRef("sharing")}
          className="legalSection"
        >
          <SectionTitle number={3} title="Sharing Your Information" />
          <Typography
            className="legalBody legalBodyBlack legalLineTight"
            component="p"
          >
            We do not sell, trade, or rent your personal information to third
            parties. We may share your data in the following limited
            circumstances:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText"
          >
            <li>
              <span className="legalEmphasis">Service providers:</span> Trusted
              third-party vendors who assist us in operating our platform, bound
              by confidentiality obligations
            </li>
            <li>
              <span className="legalEmphasis">Legal requirements:</span> When
              required by law, court order, or governmental authority
            </li>
            <li>
              <span className="legalEmphasis">Business transfers:</span> In
              connection with a merger, acquisition, or sale of all or a portion
              of our assets
            </li>
            <li>
              <span className="legalEmphasis">With your consent:</span> In any
              other cases where you have given us explicit permission
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="cookies"
          ref={setRef("cookies")}
          className="legalSection"
        >
          <SectionTitle number={4} title="Cookies & Tracking" />
          <Typography className="legalBody legalBodyBlack" component="p">
            Quest uses cookies and similar technologies to enhance your
            experience. These include:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText legalListHasBottom"
          >
            <li>
              <span className="legalEmphasis">Essential cookies:</span> Required
              for the platform to function, such as session authentication
            </li>
            <li>
              <span className="legalEmphasis">Analytics cookies:</span> Help us
              understand how users interact with our product
            </li>
            <li>
              <span className="legalEmphasis">Preference cookies:</span>{" "}
              Remember your settings and personalisation choices
            </li>
          </Box>
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            You can control or disable cookies through your browser settings.
          </Typography>
          <Box className="legalInfoBanner legalInfoBanner--yellow">
            <Box className="legalInfoBannerStripe legalInfoBannerStripe--yellow" />
            <Typography
              className="legalInfoBannerText legalInfoBannerText--yellow"
              component="p"
            >
              Note that disabling certain cookies may affect platform
              functionality.
            </Typography>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="data-retention"
          ref={setRef("data-retention")}
          className="legalSection"
        >
          <SectionTitle number={5} title="Data Retention" />
          <Typography className="legalBody legalMutedText" component="p">
            We retain your personal data for as long as your account is active
            or as needed to provide you with our services. If you close your
            account, we will delete or anonymise your personal data within 90
            days, unless we are required to retain it for legal, regulatory, or
            legitimate business purposes.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="your-rights"
          ref={setRef("your-rights")}
          className="legalSection"
        >
          <SectionTitle number={6} title="Your Rights" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Depending on your location, you may have the following rights
            regarding your personal data:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText"
          >
            <li>
              <span className="legalEmphasis">Access:</span> Request a copy of
              the personal data we hold about you
            </li>
            <li>
              <span className="legalEmphasis">Correction:</span> Request that we
              correct inaccurate or incomplete data
            </li>
            <li>
              <span className="legalEmphasis">Deletion:</span> Request deletion
              of your personal data ("right to be forgotten")
            </li>
            <li>
              <span className="legalEmphasis">Portability:</span> Receive your
              data in a structured, machine-readable format
            </li>
            <li>
              <span className="legalEmphasis">Objection:</span> Object to our
              processing of your data for marketing purposes
            </li>
            <li>
              <span className="legalEmphasis">Withdrawal of consent:</span>{" "}
              Withdraw consent at any time where processing is based on consent
            </li>
          </Box>
          <Typography
            className="legalBody legalMutedText legalBodyHasTopSmall"
            component="p"
          >
            To exercise any of these rights, please contact us at{" "}
            <a href="mailto:info@quest-q.com" className="legalLink">
              info@quest-q.com
            </a>
            .
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="data-security"
          ref={setRef("data-security")}
          className="legalSection"
        >
          <SectionTitle number={7} title="Data Security" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            We implement industry-standard security measures to protect your
            personal information from unauthorized access, disclosure,
            alteration, or destruction. These measures include encryption in
            transit (TLS), access controls, and regular security assessments.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            While we strive to use commercially acceptable means to protect your
            data, no method of transmission over the internet or electronic
            storage is 100% secure. We cannot guarantee absolute security.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="childrens-privacy"
          ref={setRef("childrens-privacy")}
          className="legalSection"
        >
          <SectionTitle number={8} title="Children's Privacy" />
          <Typography className="legalBody legalMutedText" component="p">
            Quest is not directed to individuals under the age of 16. We do not
            knowingly collect personal information from children. If you believe
            we have inadvertently collected data from a minor, please contact us
            immediately at{" "}
            <a href="mailto:info@quest-q.com" className="legalLink">
              info@quest-q.com
            </a>{" "}
            and we will take steps to delete such information.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="changes"
          ref={setRef("changes")}
          className="legalSection"
        >
          <SectionTitle number={9} title="Changes to This Policy" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            We may update this Privacy Policy from time to time to reflect
            changes in our practices or for legal and regulatory reasons. When
            we make material changes, we will notify you via email or a
            prominent notice on our platform prior to the change becoming
            effective.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            We encourage you to review this policy periodically. Your continued
            use of Quest after changes are posted constitutes your acceptance of
            the updated policy.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="contact"
          ref={setRef("contact")}
          className="legalSection legalSectionLast"
        >
          <SectionTitle number={10} title="Contact Us" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or your personal data, please contact us:
          </Typography>
          <Box className="legalContact legalMutedText">
            <Typography component="p" className="legalBody">
              <span className="legalEmphasis">Email:</span>{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Company:</span> QGroup
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SectionTitle({ number, title }) {
  return (
    <Typography component="h2" className="legalSectionTitle">
      {number}. {title}
    </Typography>
  );
}

function SubTitle({ children }) {
  return (
    <Typography component="p" className="legalSubTitle">
      {children}
    </Typography>
  );
}
